import { AsyncPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, isDevMode, NgZone, OnInit } from "@angular/core";
import { FieldType, FormlyFieldConfig, FormlyModule } from "@ngx-formly/core";
import { Panel } from "primeng/panel";
import { Tag } from "primeng/tag";
import { Tooltip } from "primeng/tooltip";
import { debounceTime, distinctUntilChanged, map, merge, Observable, startWith } from "rxjs";
import { getFormErrorPaths, hasFormErrors, isFilledByAI, isSuggested } from "../../shared/functions/form-errors";
import { PanelRememberPanelDirective } from "../../shared/primeng/panel-remember-panel/panel-remember-panel.directive";
import { outsideZone } from "../../shared/rxjs/outside-zone";
import { SettingsService } from "../../shared/services/settings.service";
import { asUnsavedChanges } from "../../shared/unsaved-changes/unsaved-changes.helper";
import { FormlyUnsavedChangesService } from "../unsaved-changes/unsaved-changes.service";

@Component({
  selector: "new-ui-formly-panel",
  templateUrl: "./new-ui-formly-panel.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormlyModule, Panel, Tag, AsyncPipe, PanelRememberPanelDirective, Tooltip],
})
export class NewUiFormlyPanelComponent extends FieldType implements OnInit {
  protected unsavedChanges$: Observable<string | null>;
  protected invalid$: Observable<boolean>;
  protected suggested$: Observable<boolean>;
  protected filledByAI$: Observable<boolean>;

  constructor(
    private readonly unsavedChangesService: FormlyUnsavedChangesService,
    private readonly ngZone: NgZone,
    private readonly settingsService: SettingsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.unsavedChanges$ = this.getUnsavedChanges();
    this.invalid$ = this.getInvalid();
    this.suggested$ = this.getSuggested();
    this.filledByAI$ = this.getFilledByAI();
  }

  private getUnsavedChanges(): Observable<string | null> {
    return this.unsavedChangesService.track(this.field).pipe(asUnsavedChanges(), outsideZone(this.ngZone));
  }

  get testId(): string {
    return "formly-panel-" + (this.props?.label ?? "").toLowerCase().replace(/\s+/g, "-");
  }

  get allTabsHidden(): boolean {
    return this.field.fieldGroup?.every((field) => field.hide);
  }

  private getInvalid(): Observable<boolean> {
    return merge(this.options.fieldChanges, this.form.statusChanges).pipe(
      startWith(undefined),
      debounceTime(100),
      map(() => this.calculateInvalidity()),
      distinctUntilChanged(),
      outsideZone(this.ngZone)
    );
  }

  private calculateInvalidity(): boolean {
    const func = (field: FormlyFieldConfig): boolean => {
      if (field.hide) return false;
      return field.fieldGroup?.length && !field.fieldArray
        ? field.fieldGroup.some((f) => func(f))
        : hasFormErrors(field.formControl);
    };

    return this.field.fieldGroup.some((field) => func(field));
  }

  private getSuggested(): Observable<boolean> {
    return merge(this.options.fieldChanges, this.form.statusChanges).pipe(
      startWith(undefined),
      debounceTime(100),
      map(() => this.calculateSuggested()),
      distinctUntilChanged(),
      outsideZone(this.ngZone)
    );
  }

  private calculateSuggested(): boolean {
    const func = (field: FormlyFieldConfig): boolean => {
      if (field.hide) return false;
      return field.fieldGroup?.length && !field.fieldArray
        ? field.fieldGroup.some((f) => func(f))
        : isSuggested(field.formControl);
    };

    return this.field.fieldGroup.some((field) => func(field));
  }

  private getFilledByAI(): Observable<boolean> {
    return merge(this.options.fieldChanges, this.form.statusChanges).pipe(
      startWith(undefined),
      debounceTime(100),
      map(() => this.calculateFilledByAI()),
      distinctUntilChanged(),
      outsideZone(this.ngZone)
    );
  }

  private calculateFilledByAI(): boolean {
    if (this.calculateInvalidity()) return false;

    const func = (field: FormlyFieldConfig): boolean =>
      field.fieldGroup?.length && !field.fieldArray
        ? field.fieldGroup.some((f) => func(f))
        : isFilledByAI(field.formControl);

    return this.field.fieldGroup.some((field) => func(field));
  }

  protected readonly showDebugInfo = isDevMode() && this.settingsService.isMasterUser;

  protected formErrorsTooltip(): string | null {
    const paths = getFormErrorPaths(this.form);
    return paths.length ? paths.join("<br>") : null;
  }

  protected get isTopLevelSection(): boolean {
    let parent = this.field.parent;
    while (parent) {
      if (parent.type === "panel") return false;
      parent = parent.parent;
    }
    return true;
  }
}
